/**
 * Internal Hook: useTOSQuery
 *
 * Fetches TOS acceptance status from backend using TanStack Query.
 * Following the pattern from ScheduledMeetingsContext.
 */

import { useQuery } from '@tanstack/react-query';
import { useApiManager } from '../../../hooks';
import { useUser } from '../../UserContext';
import { useUserPermissions } from '../../UserPermissionsContext';
import { SETTINGS_QUERY_KEYS, TOS_CONFIG } from '../helpers';
import type { TOSAcceptance } from '../types';

export const useTOSQuery = () => {
  const apiManager = useApiManager();
  const { isSuccess: isUserPermissionsReady } = useUserPermissions();
  const { isAuthenticated } = useUser();

  return useQuery<TOSAcceptance | null, Error>({
    queryKey: SETTINGS_QUERY_KEYS.tos,
    queryFn: async () => {
      if (!apiManager) {
        throw new Error('API Manager not initialized');
      }
      return await apiManager.profile.fetchTosAcceptance();
    },
    enabled: isAuthenticated && isUserPermissionsReady && TOS_CONFIG.autoFetch,
    staleTime: TOS_CONFIG.staleTime,
    gcTime: TOS_CONFIG.cacheTime,
    refetchOnWindowFocus: TOS_CONFIG.refetchOnWindowFocus,
    refetchOnReconnect: TOS_CONFIG.refetchOnReconnect,
    refetchOnMount: TOS_CONFIG.refetchOnMount,
  });
};
